import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectorRef, Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { finalize } from 'rxjs';
import { AuthService } from '../../../core/auth/auth.service';
import { httpErrorMessage } from '../../../core/utils/http-error-message';

@Component({
  selector: 'app-register',
  imports: [FormsModule, RouterLink],
  templateUrl: './register.html',
  styleUrl: './register.scss',
})
export class Register {
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);

  business = {
    name: '',
    ownerName: '',
    email: '',
    phone: '',
    otp: '',
    password: '',
    confirmPassword: '',
  };

  isSubmitting = false;
  errorMessage = '';
  statusMessage = '';
  emailSent = false;
  get isPasswordStep(): boolean {
    return this.emailSent;
  }

  onSubmit(): void {
    if (this.isSubmitting) return;

    this.errorMessage = '';
    this.statusMessage = '';
    if (this.isPasswordStep) {
      this.completeAccount();
      return;
    }

    if (!this.business.name.trim() || !this.business.ownerName.trim() || !this.business.email.trim()) {
      this.errorMessage = 'Business name, owner name and email are required.';
      this.cdr.detectChanges();
      return;
    }
    if (!/^[6-9]\d{9}$/.test(this.business.phone)) {
      this.errorMessage = 'Enter a valid 10-digit Indian mobile number.';
      this.cdr.detectChanges();
      return;
    }

    this.isSubmitting = true;
    this.cdr.detectChanges();

    this.auth
      .beginBusinessOnboarding({
        businessName: this.business.name,
        ownerFirstName: this.firstName(this.business.ownerName),
        ownerLastName: this.lastName(this.business.ownerName),
        email: this.business.email,
        phone: this.business.phone,
      })
      .pipe(finalize(() => {
        this.isSubmitting = false;
        this.cdr.detectChanges();
      }))
      .subscribe({
        next: (result) => {
          this.business.email = result.email;
          this.emailSent = result.verificationEmailSent;
          this.statusMessage = result.message;
          if (!result.verificationEmailSent) {
            this.errorMessage = 'We could not send the verification code. Please try again.';
          }
          this.cdr.detectChanges();
        },
        error: (error: HttpErrorResponse) => {
          this.errorMessage = httpErrorMessage(error, 'Unable to submit this onboarding application.');
          this.cdr.detectChanges();
        },
      });
  }

  private completeAccount(): void {
    if (!/^\d{6}$/.test(this.business.otp.trim())) {
      this.errorMessage = 'Enter the six-digit verification code sent to your email.';
      this.cdr.detectChanges();
      return;
    }
    if (!this.business.password || this.business.password.length < 8) {
      this.errorMessage = 'Create a password containing at least 8 characters.';
      this.cdr.detectChanges();
      return;
    }
    if (this.business.password !== this.business.confirmPassword) {
      this.errorMessage = 'Password confirmation does not match.';
      this.cdr.detectChanges();
      return;
    }
    this.isSubmitting = true;
    this.errorMessage = '';
    this.statusMessage = '';
    this.auth.completeBusinessAccount({
      email: this.business.email,
      otp: this.business.otp.trim(),
      password: this.business.password,
      confirmPassword: this.business.confirmPassword,
    }).pipe(finalize(() => {
      this.isSubmitting = false;
      this.cdr.detectChanges();
    })).subscribe({
      next: () => void this.router.navigate(['/login'], { queryParams: { registered: '1', email: this.business.email, verificationSent: '1' } }),
      error: (error: HttpErrorResponse) => {
        this.errorMessage = httpErrorMessage(error, 'Unable to verify the email and create the password.');
        this.cdr.detectChanges();
      },
    });
  }

  changeEmail(): void {
    if (this.isSubmitting) return;
    this.emailSent = false;
    this.business.otp = '';
    this.business.password = '';
    this.business.confirmPassword = '';
    this.errorMessage = '';
    this.statusMessage = '';
  }

  sanitizePhone(): void {
    this.business.phone = this.business.phone.replace(/\D/g, '').slice(0, 10);
  }

  private firstName(name: string): string {
    return name.trim().split(/\s+/)[0] || 'Business';
  }

  private lastName(name: string): string {
    return name.trim().split(/\s+/).slice(1).join(' ') || 'Owner';
  }

}
